/**
 * Admin visibility utilities
 *
 * Centralized location for determining admin UI visibility.
 * Used throughout the application for consistent admin feature access control.
 *
 * IMPORTANT: For authorization, always trust the server-provided `isAdmin` flag
 * from useAuth(). Client-side email checks are for display hints only and
 * are NOT secure for authorization decisions.
 *
 * Related: Issue #294, specs/037-admin-permissions/spec.md
 */

import { useAuth } from '@/services/auth';
import { isDevelopment } from './environment';

/**
 * Admin domain constant - single source of truth
 */
export const ADMIN_DOMAIN = '206mp.com';

/**
 * Check if an email belongs to the admin domain (client-side)
 *
 * WARNING: This is for display hints only. Always trust server-provided
 * isAdmin flag for actual authorization decisions.
 *
 * @param email - Email address to check
 * @returns true if email domain is exactly @206mp.com
 */
export function isAdminDomain(email: string | null | undefined): boolean {
  if (!email || typeof email !== 'string') return false;

  const normalizedEmail = email.toLowerCase().trim();
  const atIndex = normalizedEmail.lastIndexOf('@');
  if (atIndex === -1 || atIndex === normalizedEmail.length - 1) return false;

  const domain = normalizedEmail.substring(atIndex + 1);
  return domain === ADMIN_DOMAIN;
}

/**
 * Determine if admin UI elements should be visible
 *
 * Returns true if:
 * - User has explicit admin privileges (isAdmin = true), OR
 * - Application is running in development mode (dev mode bypass)
 *
 * Development environment: All authenticated users see admin features for rapid development
 * Production environment: Only verified @206mp.com emails with isAdmin = true
 *
 * NOTE: The server determines `isAdmin` based on verified @206mp.com email.
 * The dev mode bypass applies both server-side (for API access) and client-side (for UI).
 *
 * @returns true if admin UI should be shown
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const shouldShowAdmin = useShouldShowAdmin();
 *
 *   return (
 *     <>
 *       {shouldShowAdmin && <AdminPanel />}
 *     </>
 *   );
 * }
 * ```
 */
export function useShouldShowAdmin(): boolean {
  const { isAdmin } = useAuth();
  return isDevelopment() || isAdmin;
}
